import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { blogDraftReviewHtml, blogDraftShowsPost, isSitePage } from '../../../lib/blog-draft-preview';
import { fetchBlogDraftPreview } from '../../../lib/blog-draft-preview-api';
import { blogDraftPreviewReady } from '../../../lib/blog-draft-preview-ready';
import {
  blogDraftEditTooLarge,
  blogEditorDocument,
  EDITOR_CHANNEL,
  readEditorMessage,
  refreshSavedBlogDraft,
  saveBlogDraftEdit,
  useBlogDraftOpen,
  type BlogDraftEditContent,
} from '../../../lib/blog-draft-edit';
import { BlogDraftAutosave, type AutosaveStatus } from '../../../lib/blog-draft-autosave';
import { draftHeadline, draftReviewHtml } from '../../../lib/draft-preview';
import { fetchDraftPreview, fetchDraftState } from '../../../lib/draft-preview-api';
import { closeDraftReview, type DraftReviewFlow } from '../../../lib/draft-review-store';
import { readOnlyViewDocument, reviewNonce, siteViewDocument } from '../../../lib/email-review';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useIsPhone } from '../../../lib/use-is-phone';
import { useLoadBi } from '../../../lib/use-load-bi';
import { Button, Typography } from '../ui';
import styles from './BlogDraftEditor.module.css';

type Session = { srcDoc: string; baseUpdatedAt: string; source: string; editSessionId: string };
type Notice = { kind: 'stale' } | { kind: 'refused'; message: string } | { kind: 'failed' } | { kind: 'tooLarge' };

function useEditorSource(): string | null | false {
  const [source, setSource] = useState<string | null | false>(null);
  useEffect(() => {
    let live = true;
    import('@wix/blog-draft-editor').then(
      (bundle) => live && setSource(bundle.BLOG_EDITOR_SOURCE),
      () => live && setSource(false),
    );
    return () => {
      live = false;
    };
  }, []);
  return source;
}

export function BlogDraftEditor({ flow, layout }: { flow: DraftReviewFlow; layout: 'pane' | 'sheet' }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { card, conversationId } = flow;
  const ids = useMemo(
    () => ({ workspaceId: card.workspaceId, taskId: card.taskId, draftId: card.draftId }),
    [card.workspaceId, card.taskId, card.draftId],
  );
  const phone = useIsPhone();
  const [opens, setOpens] = useState(0);
  const open = useBlogDraftOpen(ids, opens);
  const opened = open.data?.status === 'open' ? open.data : null;
  const source = useEditorSource();
  const openRefused = open.data?.status === 'refused' ? open.data : null;
  const failed = open.data?.status === 'failed' || open.isError || source === false;
  useLoadBi('blog-draft-editor', { isLoading: !failed && !openRefused && (open.isLoading || source === null), isError: failed });

  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<AutosaveStatus>('saved');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [title, setTitle] = useState(card.title);
  const portRef = useRef<MessagePort | null>(null);
  const pendingRef = useRef<((content: BlogDraftEditContent | null) => void) | null>(null);
  const autosave = useRef<BlogDraftAutosave<BlogDraftEditContent> | null>(null);
  const activity = useRef(false);
  const readDirty = useRef(false);
  const action = useRef(false);
  const savedTitle = useRef(card.title);

  useEffect(() => {
    if (session || !opened || !source) return;
    const setup = { title: opened.title || card.title, ricos: opened.ricos, baseUrl: opened.baseUrl, mobile: phone };
    setTitle(setup.title);
    savedTitle.current = setup.title;
    setSession({ srcDoc: blogEditorDocument(setup, reviewNonce()), baseUpdatedAt: opened.updatedAt, source, editSessionId: crypto.randomUUID() });
  }, [session, opened, source, card.title, phone]);

  const read = useCallback(
    () =>
      new Promise<BlogDraftEditContent | null>((resolve) => {
        pendingRef.current?.(null);
        pendingRef.current = resolve;
        portRef.current?.postMessage({ type: `${EDITOR_CHANNEL}:get` });
        window.setTimeout(() => {
          if (pendingRef.current === resolve) {
            pendingRef.current = null;
            resolve(null);
          }
        }, 3000);
      }),
    [],
  );

  useEffect(() => {
    if (!session) return;
    const queue = new BlogDraftAutosave(
      session.baseUpdatedAt,
      read,
      (content, updatedAt) =>
        blogDraftEditTooLarge(content)
          ? Promise.resolve({ status: 'refused' as const, code: 'TOO_LARGE', message: '' })
          : saveBlogDraftEdit(ids, content, updatedAt, session.editSessionId),
      (next, result) => {
        setStatus(next);
        if (next === 'stale') setNotice({ kind: 'stale' });
        else if (next === 'failed')
          setNotice(
            result?.status === 'refused'
              ? result.code === 'TOO_LARGE'
                ? { kind: 'tooLarge' }
                : { kind: 'refused', message: result.message }
              : { kind: 'failed' },
          );
        else setNotice(null);
      },
      (content) => {
        setTitle(content.title);
        savedTitle.current = content.title;
        void refreshSavedBlogDraft(ids);
      },
    );
    autosave.current = queue;
    activity.current = false;
    setStatus('saved');
    return () => {
      queue.dispose();
      autosave.current = null;
    };
  }, [session, ids, read]);

  const connect = useCallback((frame: HTMLIFrameElement, editorSource: string) => {
    portRef.current?.close();
    portRef.current = null;
    setReady(false);
    const target = frame.contentWindow;
    if (!target) return;
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      const message = readEditorMessage(event.data);
      if (message?.type === `${EDITOR_CHANNEL}:ready`) setReady(true);
      else if (message?.type === `${EDITOR_CHANNEL}:change`) {
        if (message.dirty || activity.current) {
          activity.current = true;
          autosave.current?.changed();
        }
      } else if (message?.type === `${EDITOR_CHANNEL}:content`) {
        readDirty.current = message.dirty;
        pendingRef.current?.({ title: message.title, ricos: message.ricos });
        pendingRef.current = null;
      }
    };
    portRef.current = channel.port1;
    target.postMessage({ type: `${EDITOR_CHANNEL}:connect` }, '*', [channel.port2]);
    channel.port1.postMessage({ type: `${EDITOR_CHANNEL}:source`, source: editorSource });
  }, []);

  useEffect(
    () => () => {
      pendingRef.current?.(null);
      portRef.current?.close();
    },
    [],
  );

  const flushing = useRef<Promise<boolean> | null>(null);
  const flushSnapshot = useCallback((): Promise<boolean> => {
    if (flushing.current) return flushing.current;
    const flush = async () => {
      const queue = autosave.current;
      if (queue && !(await queue.flush())) return false;
      if (!ready) return true;
      const latest = await read();
      if (!latest) {
        setNotice({ kind: 'failed' });
        return false;
      }
      if (readDirty.current || activity.current) {
        queue?.changed();
        return (await queue?.flush()) ?? false;
      }
      return true;
    };
    flushing.current = flush().finally(() => {
      flushing.current = null;
    });
    return flushing.current;
  }, [read, ready]);

  useEffect(() => {
    const hide = () => {
      if (document.visibilityState === 'hidden') void flushSnapshot();
    };
    const leave = (event: BeforeUnloadEvent) => {
      if (!autosave.current?.hasChanges()) return;
      void flushSnapshot();
      event.preventDefault();
      event.returnValue = '';
    };
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('beforeunload', leave);
    return () => {
      document.removeEventListener('visibilitychange', hide);
      window.removeEventListener('beforeunload', leave);
    };
  }, [flushSnapshot]);

  const reopen = () => {
    autosave.current?.dispose();
    portRef.current?.close();
    pendingRef.current?.(null);
    pendingRef.current = null;
    portRef.current = null;
    setReady(false);
    setNotice(null);
    setOpens((n) => n + 1);
    setSession(null);
  };

  const finish = async (showPreview: boolean) => {
    if (action.current) return;
    action.current = true;
    setBusy(true);
    try {
      if (!(await flushSnapshot())) return;
      if (!showPreview) {
        closeDraftReview();
        return;
      }
      logPageClickBi({ page_name: 'chat', button_name: 'blog_draft_edit_preview', ...(conversationId ? { conversation_id: conversationId } : {}) });
      const version = autosave.current?.version();
      const state = await blogDraftPreviewReady(() => fetchDraftState(ids.workspaceId, ids.taskId, ids.draftId, 'BLOG_POST'), version);
      if (state?.wixPreview) {
        try {
          const page = await fetchBlogDraftPreview(
            ids.workspaceId,
            state.wixPreview.siteId,
            state.wixPreview.draftPostId,
            phone ? 'mobile' : 'desktop',
          );
          const latest = await fetchDraftState(ids.workspaceId, ids.taskId, ids.draftId, 'BLOG_POST');
          if (
            latest.updatedAt === version &&
            !latest.wixPreviewPending &&
            latest.wixPreview?.siteId === state.wixPreview.siteId &&
            latest.wixPreview?.draftPostId === state.wixPreview.draftPostId &&
            blogDraftShowsPost(page)
          ) {
            const html = blogDraftReviewHtml(page, savedTitle.current);
            setPreview(isSitePage(page) ? siteViewDocument(html, reviewNonce()) : readOnlyViewDocument(html));
            return;
          }
        } catch {
          setPreview(null);
        }
      }
      const page = await fetchDraftPreview(ids.workspaceId, ids.taskId, ids.draftId, 'BLOG_POST');
      setPreview(readOnlyViewDocument(draftReviewHtml({ ...card, title: savedTitle.current }, page)));
    } catch {
      setNotice({ kind: 'failed' });
    } finally {
      action.current = false;
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (preview) setPreview(null);
      else void finish(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const label = title || draftHeadline(card) || t('blogDraft.title');
  const noticeText = (shown: Notice) => {
    if (shown.kind === 'stale') return t('blogDraft.edit.stale');
    if (shown.kind === 'tooLarge') return t('blogDraft.edit.tooLarge');
    if (shown.kind === 'refused' && shown.message) return shown.message;
    return t('blogDraft.edit.failed');
  };

  return (
    <div className={`${styles.root} ${layout === 'sheet' ? styles.sheet : styles.pane}`} data-testid={`blogDraftEditor-${layout}`}>
      <header className={styles.toolbar}>
        <Typography variant="p3" color="secondary" noWrap className={styles.label} title={label}>
          {label}
        </Typography>
        <div className={styles.actions}>
          {ready && (
            <span role="status" data-testid="blogDraftEditorSaveStatus">
              {status === 'saving' || status === 'pending'
                ? t('blogDraft.edit.saving')
                : status === 'saved'
                ? t('personality.saved')
                : t('blogDraft.edit.failed')}
            </span>
          )}
          {preview ? (
            <Button size="small" variant="outlined" onClick={() => setPreview(null)} data-testid="blogDraftEditorBack">
              {t('common.back')}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              disabled={!ready || busy || status === 'stale'}
              onClick={() => void finish(true)}
              data-testid="blogDraftEditorPreview"
            >
              {t('docEditor.preview')}
            </Button>
          )}
          <Button size="small" color="accent" disabled={busy} onClick={() => void finish(false)} data-testid="blogDraftEditorDone">
            {t('cardAction.done')}
          </Button>
        </div>
      </header>
      {notice && (
        <div className={styles.notice} role="status" data-testid="blogDraftEditorNotice">
          <Typography variant="p3" color="primary">
            {noticeText(notice)}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => (notice.kind === 'stale' ? reopen() : void autosave.current?.flush())}
            data-testid="blogDraftEditorRetry"
          >
            {t(notice.kind === 'stale' ? 'blogDraft.refused.openNewer' : 'common.retry')}
          </Button>
        </div>
      )}
      <div className={styles.frameWrap} style={preview ? { display: 'none' } : undefined} inert={busy}>
        {session ? (
          <iframe
            srcDoc={session.srcDoc}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            title={label}
            className={styles.frame}
            onLoad={(event) => connect(event.currentTarget, session.source)}
            data-testid="blogDraftEditorFrame"
          />
        ) : (
          <div className={styles.status} role="status" data-testid="blogDraftEditorStatus">
            <Typography variant="p2" color="primary">
              {openRefused?.message || (failed || openRefused ? t('blogDraft.unavailable') : t('blogDraft.loading'))}
            </Typography>
          </div>
        )}
      </div>
      {preview && (
        <div className={styles.frameWrap}>
          <iframe
            srcDoc={preview}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            title={label}
            className={styles.frame}
            data-testid="blogDraftEditorPreviewFrame"
          />
        </div>
      )}
    </div>
  );
}
